import type { ResearchReadModelMeta, WorldcupResearchEvidencePackage } from '@ballpeek/api/client'
import type {
  PublicResearchHeadToHeadResponse,
  PublicResearchMatchDetailResponse,
  PublicResearchTeamProfileResponse,
  WorldcupResearchCoverageQuery,
  WorldcupResearchCoverageResponse,
  WorldcupResearchFacetsQuery,
  WorldcupResearchFacetsResponse,
  WorldcupResearchQuery,
  WorldcupResearchTeamMatchStatsResponse,
} from '~/features/worldcup-research/types'
import { authApiClient } from '~/lib/api-client'

export type PublicResearchEvidencePackage = WorldcupResearchEvidencePackage & {
  readModelMeta?: ResearchReadModelMeta
}

const publicResearchClient = authApiClient.api.research['national-team']

function cleanQuery<T extends object>(query: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(query as Record<string, unknown>)
      .filter(([, value]) => value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0))
      .map(([key, value]) => [key, Array.isArray(value) ? value.join(',') : value]),
  ) as Partial<T>
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`
    try {
      const body = await response.json() as {
        error?: { issues?: Array<{ message?: string }>, message?: string }
        message?: string
      }
      message = body.message ?? body.error?.message ?? body.error?.issues?.[0]?.message ?? message
    }
    catch {
      // Keep the status-based fallback when the backend returns a non-JSON error.
    }
    throw new Error(message)
  }
  return response.json() as Promise<T>
}

async function parseText(response: Response): Promise<string> {
  if (!response.ok)
    await parseJson<never>(response)
  return response.text()
}

export async function listPublicResearchCoverage(filters: WorldcupResearchCoverageQuery) {
  const response = await publicResearchClient.coverage.$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchCoverageResponse>(response)
}

export async function listPublicResearchFacets(filters: WorldcupResearchFacetsQuery) {
  const response = await publicResearchClient.facets.$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchFacetsResponse>(response)
}

export async function listPublicResearchTeamMatchStats(filters: WorldcupResearchQuery) {
  const response = await publicResearchClient['team-match-stats'].$get({
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<WorldcupResearchTeamMatchStatsResponse>(response)
}

type HeadToHeadFilters = Omit<WorldcupResearchQuery, 'team' | 'opponent'> & { team: string, opponent: string }

export async function getPublicResearchTeamProfile(teamSlug: string, filters: Partial<WorldcupResearchQuery> = {}) {
  const response = await publicResearchClient.profiles[':teamSlug'].$get({
    param: { teamSlug },
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<PublicResearchTeamProfileResponse>(response)
}

export async function getPublicResearchHeadToHead(filters: HeadToHeadFilters) {
  const response = await publicResearchClient['head-to-head'].$get({
    query: { ...cleanQuery(filters), team: filters.team, opponent: filters.opponent },
    header: { Authorization: '' },
  })
  return parseJson<PublicResearchHeadToHeadResponse>(response)
}

export async function getPublicResearchMatchDetail(matchId: string) {
  const response = await (publicResearchClient.matches as any)[':matchId'].$get({
    param: { matchId },
    header: { Authorization: '' },
  })
  return parseJson<PublicResearchMatchDetailResponse>(response)
}

export async function exportPublicResearchTeamProfileCsv(teamSlug: string, filters: Partial<WorldcupResearchQuery> = {}) {
  const response = await publicResearchClient.profiles[':teamSlug'].export.$get({
    param: { teamSlug },
    query: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseText(response)
}

export async function exportPublicResearchHeadToHeadCsv(filters: HeadToHeadFilters) {
  const response = await publicResearchClient['head-to-head'].export.$get({
    query: { ...cleanQuery(filters), team: filters.team, opponent: filters.opponent },
    header: { Authorization: '' },
  })
  return parseText(response)
}

export async function searchPublicResearchTeamMatchStatsEvidence(filters: WorldcupResearchQuery) {
  const response = await publicResearchClient.evidence['search-team-match-stats'].$post({
    json: cleanQuery(filters),
    header: { Authorization: '' },
  })
  return parseJson<PublicResearchEvidencePackage>(response)
}
